import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  tipoMeta, PRIORIDAD_LABEL, prioridadMeta, ESTADO_LABEL, RESULTADO_LABEL, SIGUIENTES, elapsed, CAPACIDAD_LABEL, TIPOS_TECNICOS,
} from "@/lib/alarmMeta";
import { calcEdad, fmtTimestamp } from "@/lib/format";
import {
  Phone, Copy, Check, MapPin, HeartPulse, Clock, ShieldAlert, UserCog, History, ListChecks, ImageOff, Image as ImageIcon, Wrench,
} from "lucide-react";

function ImagenTermica({ eventoId }) {
  // 4 estados explícitos, nunca un espacio en blanco. NO bloquea el plan de acción.
  const [estado, setEstado] = useState("cargando");
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!eventoId) { setEstado("no_disponible"); return; }
    let objUrl = null; let vivo = true;
    setEstado("cargando"); setUrl(null);
    api.get(`/eventos/${eventoId}/imagen`, { responseType: "blob" })
      .then((r) => { if (!vivo) return; objUrl = URL.createObjectURL(r.data); setUrl(objUrl); setEstado("ok"); })
      .catch(async (e) => {
        if (!vivo) return;
        let est = "fallo";
        try {
          const txt = e?.response?.data ? await e.response.data.text() : null;
          if (txt) est = (JSON.parse(txt).estado) || "no_disponible";
        } catch (_) { est = e?.response?.status === 404 ? "no_disponible" : "fallo"; }
        setEstado(est);
      });
    return () => { vivo = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [eventoId]);

  const MSG = {
    cargando: "Cargando imagen…",
    no_disponible: "Sin imagen para este evento",
    expirada: "Imagen expirada por política de retención (7 días)",
    fallo: "No se pudo cargar la imagen",
  };
  return (
    <div data-testid="imagen-termica" className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Imagen térmica</p>
      {estado === "ok" ? (
        <img src={url} alt="Imagen térmica del evento" data-testid="imagen-termica-img"
          onError={() => setEstado("fallo")}
          className="max-h-64 rounded-lg border border-slate-200 dark:border-slate-700" />
      ) : (
        <div data-testid={`imagen-termica-${estado}`}
          className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-3 py-4 text-sm text-slate-500 dark:text-slate-400">
          {estado === "cargando" ? <ImageIcon strokeWidth={1.6} className="h-4 w-4 animate-pulse" /> : <ImageOff strokeWidth={1.6} className="h-4 w-4" />}
          {MSG[estado] || MSG.fallo}
        </div>
      )}
    </div>
  );
}

function CallButtons({ telefono, idx }) {
  if (!telefono) return <span className="text-sm text-slate-400">Sin teléfono</span>;
  const copy = () => {
    navigator.clipboard?.writeText(telefono);
    toast.success("Número copiado");
  };
  return (
    <div className="flex items-center gap-1.5">
      <a
        data-testid={`btn-llamar-${idx}`}
        href={`tel:${telefono.replace(/\s+/g, "")}`}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[#01CBE3] px-3 py-1.5 text-sm font-semibold text-[#012472] hover:bg-[#01B5CB] transition-colors"
      >
        <Phone strokeWidth={1.9} className="h-4 w-4" /> Llamar
      </a>
      <button
        data-testid={`btn-copiar-${idx}`}
        onClick={copy}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-600 px-2.5 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        title="Copiar número"
      >
        <Copy strokeWidth={1.7} className="h-4 w-4" />
      </button>
    </div>
  );
}

function NotaGestionInput({ onGuardar, busy }) {
  const [txt, setTxt] = useState("");
  const guardar = async () => { const t = txt.trim(); if (!t) return; await onGuardar(t); setTxt(""); };
  return (
    <div>
      <textarea data-testid="input-nota-gestion" value={txt} onChange={(e) => setTxt(e.target.value)} rows={2}
        placeholder="Registra la acción tomada (llamada al técnico, visita, confirmación). Se guarda en el registro y NO se borra al autocerrarse."
        className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 text-sm text-slate-700 dark:text-slate-200" />
      <div className="mt-2">
        <Button data-testid="btn-guardar-nota" onClick={guardar} disabled={busy || !txt.trim()}
          className="bg-[#012472] hover:bg-[#012472]/90 text-white font-semibold">Guardar nota</Button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#012472] dark:text-[#80B3FF]">
        <Icon strokeWidth={1.8} className="h-4 w-4" /> {title}
      </h3>
      {children}
    </section>
  );
}

export default function AlarmDetail({ detail, nowMs, onTake, onTransition, onStep, onVerify, onCloseClick, busy,
  onLiberar, onSolicitarApoyo, onAtenderTambien, takeBlockedAlta, incidenciaTecnica, notaDraft, onNotaDraft, onNotaGestion }) {
  const a = detail;
  const { label: tipoLabel, Icon } = tipoMeta(a.tipo);
  const esTecnica = a.es_tecnica ?? TIPOS_TECNICOS.has(a.tipo);
  const persona = a.persona || {};
  const sitio = a.sitio || {};
  const isRecibida = a.estado === "recibida";
  const isCerrada = a.estado === "cerrada";
  const enVerificacion = a.estado === "en_verificacion";
  const coral = isRecibida && !esTecnica;
  const brick = isRecibida && esTecnica;
  const accentText = brick ? "text-[#9B2C2C] dark:text-[#F2B8B8]" : coral ? "text-[#FE6A55]" : "text-slate-700 dark:text-slate-200";
  const headerBorder = coral ? "border-[#FE6A55]/40 bg-[#FE6A55]/10"
    : brick ? "border-[#9B2C2C]/40 bg-[#9B2C2C]/5 dark:bg-[#9B2C2C]/15"
    : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800";
  const tipoHeadline = a.tipo === "cobertura_perdida" && a.capacidad
    ? `${tipoLabel} · ${CAPACIDAD_LABEL[a.capacidad] || a.capacidad}` : tipoLabel;
  const agrupados = a.eventos_agrupados || a.eventos_relacionados || [];
  const edad = calcEdad(persona.fecha_nacimiento);
  const pasos = (a.action_plan_congelado || {}).pasos || [];
  const contactos = a.contactos_resueltos || [];
  const cuidadora = a.cuidadora_en_turno;
  const domicilio = [sitio.direccion, sitio.ciudad].filter(Boolean).join(", ");
  const siguientes = SIGUIENTES[a.estado] || [];

  return (
    <div className="space-y-4" data-testid="alarm-detail">
      {/* Encabezado */}
      <div className={`rounded-xl border p-5 ${headerBorder}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <Icon strokeWidth={1.9} className={`h-7 w-7 ${accentText}`} />
              <div>
                {esTecnica ? (
                  <>
                    <p className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                      <Wrench className="h-3.5 w-3.5" /> Incidencia técnica
                    </p>
                    <h2 className="text-2xl font-semibold uppercase tracking-wide text-[#9B2C2C] dark:text-[#F2B8B8]">{tipoHeadline}</h2>
                    {sitio.nombre && <p className="mt-1 text-base font-medium text-slate-700 dark:text-slate-200">{sitio.nombre}</p>}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{tipoLabel}</p>
                    <h2 className="text-2xl font-semibold text-[#012472] dark:text-slate-100">
                      {persona.nombre_completo || a.persona_nombre || "Persona no identificada"}
                      {edad != null && <span className="text-lg font-normal text-slate-500 dark:text-slate-400"> · {edad} años</span>}
                    </h2>
                  </>
                )}
              </div>
            </div>
            {!esTecnica && persona.condiciones && (
              <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                <HeartPulse strokeWidth={1.7} className="h-4 w-4 mt-0.5 text-slate-400" /> {persona.condiciones}
              </p>
            )}
            {domicilio && (
              <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                <MapPin strokeWidth={1.7} className="h-4 w-4 mt-0.5 text-slate-400" /> {domicilio}
                {sitio.referencias ? ` — ${sitio.referencias}` : ""}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className={`tabular-nums text-2xl font-bold ${brick ? "text-[#9B2C2C] dark:text-[#F2B8B8]" : coral ? "text-[#FE6A55]" : "text-slate-700 dark:text-slate-200"}`}>
              {elapsed(a.hora_recepcion_servidor, nowMs)}
            </p>
            <span className={`inline-block mt-1 rounded-md px-2.5 py-0.5 text-xs font-semibold ${
              brick ? "bg-[#9B2C2C]/15 text-[#9B2C2C] dark:text-[#F2B8B8]"
                : coral ? "bg-[#FE6A55]/15 text-[#FE6A55]"
                : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"
            }`}>
              {isRecibida ? "Sin tomar" : (ESTADO_LABEL[a.estado] || a.estado)}
            </span>
            <p className="mt-1 text-xs">
              {(() => { const pm = prioridadMeta(a.prioridad); const PI = pm.Icon; return (
                <span className={`inline-flex items-center gap-1 font-semibold ${pm.text}`}>
                  <PI strokeWidth={2} className="h-3.5 w-3.5" /> Prioridad {pm.label}
                </span>
              ); })()}
            </p>
            {a.operador_nombre && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Operador: {a.operador_nombre}</p>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="mt-4 flex flex-wrap gap-2">
          {isRecibida && !takeBlockedAlta && (
            <Button
              data-testid="btn-tomar-alarma"
              onClick={onTake}
              disabled={busy}
              className="bg-[#01CBE3] hover:bg-[#01B5CB] text-[#012472] font-semibold"
            >
              Tomar alarma
            </Button>
          )}
          {isRecibida && takeBlockedAlta && (
            <div data-testid="segunda-alta" className="w-full rounded-lg border border-[#9B2C2C]/40 bg-[#9B2C2C]/10 p-3">
              <p className="text-sm font-medium text-[#9B2C2C] dark:text-[#F2B8B8]">
                Ya tienes una ALTA en atención. Puedes pedir apoyo Y atender esta también (entrarás en sobrecarga, queda auditado).
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button data-testid="btn-solicitar-apoyo" onClick={onSolicitarApoyo} disabled={busy}
                  className="bg-[#012472] hover:bg-[#012472]/90 text-white font-semibold">Solicitar apoyo</Button>
                <Button data-testid="btn-atender-tambien" onClick={onAtenderTambien} disabled={busy}
                  className="bg-[#9B2C2C] hover:bg-[#822424] text-white font-semibold">Atender también · sobrecarga</Button>
              </div>
            </div>
          )}
          {!isRecibida && !isCerrada && siguientes.map(([estado, label]) => (
            <Button
              key={estado}
              data-testid={`btn-transicion-${estado}`}
              variant="outline"
              onClick={() => onTransition(estado)}
              disabled={busy}
              className="border-[#012472]/30 text-[#012472] dark:text-[#80B3FF] dark:border-slate-600"
            >
              {label}
            </Button>
          ))}
          {!isRecibida && !isCerrada && (
            <Button
              data-testid="btn-abrir-cerrar"
              onClick={onCloseClick}
              disabled={busy}
              className="bg-[#012472] hover:bg-[#012472]/90 text-white"
            >
              Cerrar alarma
            </Button>
          )}
          {!isRecibida && !isCerrada && onLiberar && (
            <Button data-testid="btn-liberar" variant="outline" onClick={onLiberar} disabled={busy}
              className="border-amber-400 text-amber-700 dark:text-amber-300 dark:border-amber-600">
              Liberar
            </Button>
          )}
          {!isRecibida && !isCerrada && a.prioridad === "alta" && onSolicitarApoyo && (
            <Button data-testid="btn-solicitar-apoyo-2" variant="outline" onClick={onSolicitarApoyo} disabled={busy}
              className="border-[#012472]/40 text-[#012472] dark:text-[#80B3FF] dark:border-slate-600">
              Solicitar apoyo
            </Button>
          )}
          {isCerrada && (
            <span data-testid="cerrada-badge" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <Check strokeWidth={2} className="h-4 w-4" /> Cerrada: {RESULTADO_LABEL[a.clasificacion] || a.clasificacion}
              {a.causa_falsa ? ` (${a.causa_falsa})` : ""}
            </span>
          )}
        </div>

        {/* Verificación de audio (Bloque B): dos resultados posibles */}
        {a.requiere_verificacion && enVerificacion && !a.verificacion && (
          <div data-testid="audio-verificacion" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Excepción de audio en verificación. Llama al sitio o a la cuidadora y registra el resultado.
              {a.sla?.audio_verificacion_seg ? ` Si no registras en ${a.sla.audio_verificacion_seg}s, escala a ALTA automáticamente.` : ""}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button data-testid="btn-verif-ok" onClick={() => onVerify(true, "contacto logrado")} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                Contacto logrado — todo bien
              </Button>
              <Button data-testid="btn-verif-fallo" onClick={() => onVerify(false, "no se logró contacto")} disabled={busy}
                className="bg-[#9B2C2C] hover:bg-[#822424] text-white font-semibold">
                No hubo contacto — escalar a ALTA
              </Button>
            </div>
          </div>
        )}

        {/* Banner de escalamiento */}
        {a.escalado && (
          <div data-testid="escalado-banner" className="mt-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300">
            Escalada a ALTA · {a.escalado.motivo === "contacto_no_logrado" ? "no se logró contacto en la verificación" : "verificación no completada a tiempo"}
          </div>
        )}

        {/* Conteo de eventos agrupados en el caso */}
        {agrupados.length > 1 && (
          <div data-testid="eventos-agrupados" className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {agrupados.length} eventos agrupados en este caso
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {agrupados.map((g, i) => (
                <span key={i} className="rounded-md bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-300 tabular-nums">
                  {g.hora ? fmtTimestamp(g.hora) : `evento ${i + 1}`}
                </span>
              ))}
            </div>
          </div>
        )}
        {!esTecnica && <ImagenTermica eventoId={a.evento_disparador_id} />}
      </div>

      {/* Incidencia técnica del mismo sitio (conecta lo técnico con lo clínico) */}
      {incidenciaTecnica && (
        <div data-testid="incidencia-tecnica-sitio" className="rounded-xl border border-[#9B2C2C]/40 bg-[#9B2C2C]/10 px-4 py-2.5 text-sm font-medium text-[#9B2C2C] dark:text-[#F2B8B8]">
          ⚠ {incidenciaTecnica}
        </div>
      )}

      {/* Notas de la gestión. Técnicas: PERSISTIDAS (sobreviven al autocierre). Persona: borrador local que precarga el cierre. */}
      {esTecnica ? (
        <Section icon={ListChecks} title="Notas de la gestión" testId="notas-gestion">
          {(a.notas_gestion || []).length > 0 ? (
            <ul data-testid="lista-notas-gestion" className="space-y-2 mb-3">
              {a.notas_gestion.map((n, i) => (
                <li key={i} className="rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2.5">
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{n.texto}</p>
                  <p className="mt-1 text-[11px] text-slate-400 tabular-nums">{n.por} · {fmtTimestamp(n.en)}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400 mb-3">Aún no hay notas. Si una falla es intermitente, estas notas le dicen a la siguiente monitorista que ya se reportó antes.</p>
          )}
          {!isCerrada && onNotaGestion && <NotaGestionInput onGuardar={onNotaGestion} busy={busy} />}
          {isCerrada && <p className="text-xs text-slate-400">Incidencia cerrada — las notas quedan en el registro.</p>}
        </Section>
      ) : (!isCerrada && onNotaDraft && (
        <Section icon={ListChecks} title="Notas de la gestión (borrador)" testId="notas-draft">
          <textarea data-testid="input-notas-draft" value={notaDraft || ""} onChange={(e) => onNotaDraft(e.target.value)}
            rows={2} placeholder="Escribe mientras gestionas; se guarda solo en este equipo y precarga el cierre."
            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 text-sm text-slate-700 dark:text-slate-200" />
        </Section>
      ))}

      {/* Action plan congelado */}
      <Section icon={ListChecks} title="Plan de acción" testId="action-plan">
        {pasos.length === 0 ? (
          <p className="text-sm text-slate-400">No hay plantilla de plan de acción para este tipo de alarma.</p>
        ) : (
          <ol className="space-y-2">
            {pasos.map((p) => (
              <li key={p.orden} className="flex items-start gap-3">
                <button
                  data-testid={`paso-${p.orden}`}
                  onClick={() => !p.ejecutado && !isCerrada && onStep(p.orden)}
                  disabled={p.ejecutado || isCerrada || isRecibida || busy}
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                    p.ejecutado
                      ? "bg-[#01CBE3] border-[#01CBE3] text-[#012472]"
                      : "border-slate-300 dark:border-slate-500 hover:border-[#01CBE3]"
                  } ${(isCerrada || isRecibida) && !p.ejecutado ? "opacity-40 cursor-not-allowed" : ""}`}
                  title={isRecibida ? "Toma la alarma para ejecutar pasos" : "Marcar como ejecutado"}
                >
                  {p.ejecutado && <Check strokeWidth={3} className="h-3.5 w-3.5" />}
                </button>
                <div className="min-w-0">
                  <p className={`text-sm ${p.ejecutado ? "text-slate-500 dark:text-slate-400 line-through" : "text-slate-700 dark:text-slate-200"}`}>
                    {p.texto}
                  </p>
                  {p.ejecutado && (
                    <p className="text-xs text-slate-400">
                      {p.ejecutado_por} · {fmtTimestamp(p.ejecutado_en)}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {/* Cuidadora en turno */}
      {cuidadora && (
        <Section icon={UserCog} title="Cuidadora programada en turno" testId="cuidadora-detail">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-slate-800 dark:text-slate-100">{cuidadora.nombre}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {cuidadora.tipo === "planta" ? "De planta (24/7)" : "Por horas"} · {cuidadora.telefono}
              </p>
            </div>
            <CallButtons telefono={cuidadora.telefono} idx="cuidadora" />
          </div>
        </Section>
      )}

      {/* Contactos de escalamiento */}
      <Section icon={ShieldAlert} title="Contactos en orden de escalamiento" testId="contactos-escalamiento">
        {contactos.length === 0 ? (
          <p className="text-sm text-slate-400">No hay contactos capturados para esta persona.</p>
        ) : (
          <ul className="space-y-3">
            {contactos.map((rel, i) => (
              <li key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-700 pb-3 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 dark:text-slate-100">
                    <span className="tabular-nums text-slate-400 mr-1.5">{rel.orden || i + 1}.</span>
                    {rel.contacto?.nombre_completo || "—"}
                    <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">{rel.relacion_familiar}</span>
                  </p>
                  {rel.autoridad && <p className="text-xs text-slate-500 dark:text-slate-400">{rel.autoridad}</p>}
                </div>
                <CallButtons telefono={rel.contacto?.telefono} idx={i} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Historial */}
      <Section icon={History} title="Historial reciente de esta persona" testId="historial">
        {(!a.historial || a.historial.length === 0) ? (
          <p className="text-sm text-slate-400">Sin alarmas previas registradas.</p>
        ) : (
          <ul className="space-y-2">
            {a.historial.map((h) => (
              <li key={h.id} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 dark:text-slate-200">{tipoMeta(h.tipo).label}</span>
                <span className="text-slate-400">
                  {fmtTimestamp(h.hora_recepcion_servidor)}
                  {h.clasificacion ? ` · ${RESULTADO_LABEL[h.clasificacion] || h.clasificacion}` : ` · ${ESTADO_LABEL[h.estado] || h.estado}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Tiempos */}
      <Section icon={Clock} title="Tiempos de la gestión" testId="tiempos">
        <div className="grid grid-cols-2 gap-y-1.5 text-sm">
          <span className="text-slate-500 dark:text-slate-400">Recepción</span>
          <span className="text-slate-700 dark:text-slate-200">{fmtTimestamp(a.hora_recepcion_servidor)}</span>
          <span className="text-slate-500 dark:text-slate-400">Tomada</span>
          <span className="text-slate-700 dark:text-slate-200">{a.tomada_en ? fmtTimestamp(a.tomada_en) : "—"}</span>
          <span className="text-slate-500 dark:text-slate-400">Primer contacto</span>
          <span className="text-slate-700 dark:text-slate-200">{a.primer_contacto_en ? fmtTimestamp(a.primer_contacto_en) : "—"}</span>
          <span className="text-slate-500 dark:text-slate-400">Cierre</span>
          <span className="text-slate-700 dark:text-slate-200">{a.cierre_en ? fmtTimestamp(a.cierre_en) : "—"}</span>
        </div>
      </Section>
    </div>
  );
}
